import styled from 'styled-components';

/* ─── Shell da página ──────────────────────────────────────────────────── */

export const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  /* Véu leve por cima da imagem de fundo, só para dar legibilidade. */
  background: linear-gradient(
    180deg,
    rgba(18, 18, 18, 0.1) 0%,
    rgba(18, 18, 18, 0.26) 100%
  );
`;

/* Todas as faixas usam a mesma grade da área principal, para os elementos
   ficarem alinhados verticalmente com o card. */
const innerGrid = `
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 1.5rem;

  @media (max-width: 1023px) {
    padding: 0 1rem;
  }
`;

export const TopBar = styled.header`
  flex: 0 0 auto;
  min-height: 64px;
  display: flex;
  align-items: center;
  /* Acima da barra de etapas, senão o menu de idiomas abre por baixo dela. */
  position: relative;
  z-index: 30;

  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(14px) saturate(120%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.55);
  box-shadow: 0 1px 12px rgba(18, 18, 18, 0.1);
`;

export const TopBarInner = styled.div`
  ${innerGrid}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 720px) {
    flex-wrap: wrap;
    gap: 0.5rem;
    padding-top: 0.6rem;
    padding-bottom: 0.6rem;
  }
`;

export const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex: 0 0 auto;

  img {
    width: auto;
    display: block;
    height: 28px;
  }

  /* O ícone do SGRLock é mais "quadrado" que a marca da Odeen — sem um ajuste
     de altura ele fica visualmente menor que o vizinho. */
  img:first-child {
    height: 40px;
  }

  @media (max-width: 720px) {
    img {
      height: 22px;
    }

    img:first-child {
      height: 32px;
    }
  }
`;

export const BrandDivider = styled.span`
  width: 1px;
  height: 24px;
  background: var(--stone-300);
`;

export const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;

  button {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    flex: 0 0 auto;

    border: 1px solid var(--border);
    background: rgba(255, 255, 255, 0.7);
    border-radius: var(--radius-full);
    padding: 0.4rem 0.85rem;
    font-family: var(--font-sans);
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--stone-700);
    transition: border-color 0.15s, background-color 0.15s, color 0.15s;

    &:hover:not(:disabled) {
      border-color: var(--stone-400);
      background: #fff;
      color: var(--foreground);
    }

    /* Atalho para um tipo de consulta não contratado. */
    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    &[data-variant='danger']:hover {
      border-color: var(--feedback-danger);
      color: var(--feedback-danger);
    }
  }

  @media (max-width: 720px) {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
`;

export const UserBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.25;
  min-width: 0;
  margin-right: 0.25rem;

  strong {
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--foreground);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 220px;
  }

  span {
    font-size: 0.72rem;
    color: var(--muted-foreground);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 220px;
  }

  @media (max-width: 720px) {
    align-items: flex-start;
    order: -1;
    width: 100%;
  }
`;

/* O LanguageSwitch se posiciona sozinho no canto — aqui ele volta ao fluxo. */
export const LangSlot = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  > div {
    position: static;
  }
`;

/* ─── Barra de etapas ──────────────────────────────────────────────────── */

export const StepNav = styled.nav`
  flex: 0 0 auto;
  min-height: 58px;
  display: flex;
  align-items: center;

  background: rgba(255, 255, 255, 0.62);
  backdrop-filter: blur(12px) saturate(115%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);
`;

export const StepNavInner = styled.div`
  ${innerGrid}
  display: flex;
  align-items: center;
  gap: 1.25rem;

  @media (max-width: 720px) {
    gap: 0.75rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
`;

export const StepMeta = styled.span`
  flex: 0 0 auto;
  font-family: var(--font-mono);
  font-size: 0.72rem;
  color: var(--muted-foreground);
  white-space: nowrap;
`;

export const SummaryButton = styled.button`
  flex: 0 0 auto;
  display: none;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.75);
  border-radius: var(--radius-full);
  padding: 0.35rem 0.8rem;
  font-family: var(--font-sans);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--stone-700);

  &:hover {
    border-color: var(--stone-400);
    color: var(--foreground);
  }

  /* Só aparece quando o painel lateral não cabe. */
  @media (max-width: 1023px) {
    display: inline-flex;
    margin-left: auto;
  }
`;

/* ─── Área principal ───────────────────────────────────────────────────── */

export const Main = styled.main<{ hasAside: boolean }>`
  flex: 1 1 auto;
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 1.25rem;

  @media (max-width: 1023px) {
    padding: 1rem;
  }
`;

export const Aside = styled.aside`
  flex: 0 0 320px;
  width: 320px;
  position: sticky;
  top: 1.5rem;

  /* Sendo sticky, o painel não acompanha a rolagem da página: o que passar da
     altura visível ficaria inalcançável. Limitando aqui, a rolagem acontece
     dentro do próprio painel (importa quando a lista de serviços é longa e a
     janela é baixa, tipo 1366x768). */
  max-height: calc(100vh - 3rem);
  display: flex;
  flex-direction: column;

  @media (max-width: 1023px) {
    display: none;
  }
`;

/* ─── Card das etapas (usado por cada tela do assistente) ──────────────── */

export const Content = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  max-width: 860px;
  min-height: 300px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  padding: 0 1.5rem 1.5rem;
  position: relative;

  /* Vidro flutuante: translúcido sobre a imagem de fundo, sem exagerar. */
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(16px) saturate(115%);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: var(--radius-lg);
  box-shadow: 0 14px 34px rgba(18, 18, 18, 0.18),
    0 2px 8px rgba(18, 18, 18, 0.08);

  /* O título vira o cabeçalho do card, sangrando até as bordas. O texto fica
     alinhado com o corpo; a barra âmbar vive na margem. */
  > h1 {
    width: calc(100% + 3rem);
    margin: 0 -1.5rem;
    min-height: 56px;
    display: flex;
    align-items: center;
    padding: 0.75rem 7.5rem 0.75rem 1.5rem;
    border-bottom: 1px solid var(--border);
    text-align: left;
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.3;
    color: var(--foreground);
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 0.6rem;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 1.15em;
      border-radius: 2px;
      background: var(--primary);
    }
  }

  /* Ação principal da etapa vira o rodapé do card. */
  > button:last-child {
    align-self: flex-end;
    margin-top: auto;
  }

  @media (max-width: 1023px) {
    max-width: 100%;
  }

  @media (max-width: 600px) {
    min-height: 0;
    border-radius: var(--radius-md);

    > h1 {
      font-size: 0.95rem;
      padding-right: 5.5rem;
    }
  }
`;

/* Âncora de voltar uma etapa — ancorada no cabeçalho do card. Precisa de
   z-index porque o <h1> do cabeçalho vem depois dela no DOM e, sem isso,
   fica por cima e engole o clique. */
export const GoBack = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  min-height: 56px;
  padding: 0 1.5rem;

  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.4rem;

  font-family: var(--font-sans);
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--primary-hover);
  /* Os rótulos existentes vêm em minúsculas ("voltar"). */
  text-transform: capitalize;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  @media (max-width: 600px) {
    padding: 0 1rem;
    font-size: 0.78rem;
  }
`;
